"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SurveillanceImpl = void 0;
const utils_1 = require("../utils");
const luxon_1 = require("luxon");
class SurveillanceImpl {
    constructor(idx, surv, hasSpo2o2, hasDispoOxy, isSpo2AA, commonGlyUnit) {
        this.format = (elem, custom = []) => {
            const temp = [null, undefined, 'u', ...custom].includes(elem) ? null : elem;
            if (temp)
                this.isEmpty = false;
            return temp;
        };
        this.formatOnly = (elem, custom = []) => ([null, undefined, 'u', ...custom].includes(elem) ? null : elem);
        this.formatZero = (elem, custom = []) => {
            const temp = [null, undefined, 'u', ...custom].includes(elem) ? null : elem === 0 ? '0' : elem;
            if (temp)
                this.isEmpty = false;
            return temp;
        };
        this.type = 'surveillance';
        this.index = idx;
        this.date = surv.surv_heure ? luxon_1.DateTime.fromISO(surv.surv_heure).setZone('Europe/Paris').setLocale('fr-fr') : null;
        this.heure = SurveillanceImpl.extractHours(surv);
        this.minute = SurveillanceImpl.extractMinutes(surv);
        this.isEmpty = true;
        this.o2aa = null;
        this.o2o2 = null;
        this.spo2 = this.format(surv.spo2, [...utils_1.EMPTY_STRING_ARRAY, ...utils_1.NEGATIVE_ARRAY]) ||
            this.format(surv.spo2, [...utils_1.EMPTY_STRING_ARRAY, ...utils_1.NEGATIVE_ARRAY]);
        this.oxy_debit = this.format(surv.oxygen_debit, [...utils_1.EMPTY_STRING_ARRAY, ...utils_1.ZERO_ARRAY, ...utils_1.NEGATIVE_ARRAY]);
        this.oxy_debit_unit = this.format(surv.oxygen_debit_unit, [...utils_1.EMPTY_STRING_ARRAY, ...utils_1.NEGATIVE_ARRAY]);
        this.dispositif_oxygeno = this.format(surv.dispositif_oxygeno, [...utils_1.EMPTY_STRING_ARRAY, ...utils_1.NEGATIVE_ARRAY]);
        this.spo2_with_o2 =
            this.format(surv.spo2_with_o2, [...utils_1.EMPTY_STRING_ARRAY, ...utils_1.NEGATIVE_ARRAY]) ||
                this.format(surv.spo2_sous_o2, [...utils_1.EMPTY_STRING_ARRAY, ...utils_1.NEGATIVE_ARRAY]);
        let unit = this.oxy_debit_unit === 0 ? 'L/min' : this.oxy_debit_unit === 1 ? '%' : '';
        let debit = this.debit_o2 && this.debit_o2 !== -1 ? this.debit_o2 : this.oxy_debit && this.oxy_debit !== -1 ? this.oxy_debit : null;
        this.debit_o2_str_export_textual = debit ? `${debit} ${unit}` : null;
        this.debit_o2 = this.format(surv.debit_o2, [...utils_1.EMPTY_STRING_ARRAY, ...utils_1.ZERO_ARRAY, ...utils_1.NEGATIVE_ARRAY]);
        this.inconscience = this.formatZero(surv.conscience);
        this.fr = this.formatZero(surv.fr, [...utils_1.NEGATIVE_ARRAY, ...utils_1.EMPTY_STRING_ARRAY]);
        this.spco =
            this.formatZero(surv.spco_decimal, [...utils_1.NEGATIVE_ARRAY, ...utils_1.EMPTY_STRING_ARRAY]) ||
                this.formatZero(surv.spco, [...utils_1.NEGATIVE_ARRAY, ...utils_1.EMPTY_STRING_ARRAY]);
        this.etco2 = this.formatZero(surv.etco2);
        this.peak_flow = this.formatZero(surv.peak_flow);
        this.respirateur_fr = this.format(surv.respirateur_fr);
        this.respirateur_fio2 = this.format(surv.respirateur_fio2);
        this.respirateur_vt = this.format(surv.respirateur_vt);
        this.respirateur_pep = this.format(surv.respirateur_pep);
        this.fc = this.formatZero(surv.fc, [...utils_1.NEGATIVE_ARRAY, ...utils_1.EMPTY_STRING_ARRAY]);
        this.fc_type = this.format(surv.fc_type);
        this.pabd = this.formatZero(surv.pabd, [...utils_1.NEGATIVE_ARRAY, ...utils_1.EMPTY_STRING_ARRAY]);
        this.pambd = this.formatZero(surv.pambd, [...utils_1.NEGATIVE_ARRAY, ...utils_1.EMPTY_STRING_ARRAY]);
        this.pabg = this.formatZero(surv.pabg, [...utils_1.NEGATIVE_ARRAY, ...utils_1.EMPTY_STRING_ARRAY]);
        this.pambg = this.formatZero(surv.pambg, [...utils_1.NEGATIVE_ARRAY, ...utils_1.EMPTY_STRING_ARRAY]);
        this.pamd = (0, utils_1.computePam)(this.pabd) || null;
        this.pamg = (0, utils_1.computePam)(this.pabg) || null;
        this.is_pam_entry_by_user = this.format(surv.is_pam_entry_by_user);
        this.tension_moyenne = this.formatZero(surv.tension_moyenne, [...utils_1.NEGATIVE_ARRAY, ...utils_1.EMPTY_STRING_ARRAY]);
        this.tension_manuelle_systolique = this.formatZero(surv?.tension_manuelle_systolique, [
            ...utils_1.NEGATIVE_ARRAY,
            ...utils_1.EMPTY_STRING_ARRAY,
        ]);
        this.tension_manuelle_systolique_gauche = this.formatZero(surv?.tension_manuelle_systolique_gauche, [
            ...utils_1.NEGATIVE_ARRAY,
            ...utils_1.EMPTY_STRING_ARRAY,
        ]);
        this.tension_manuelle_systolique_droite = this.formatZero(surv?.tension_manuelle_systolique_droite, [
            ...utils_1.NEGATIVE_ARRAY,
            ...utils_1.EMPTY_STRING_ARRAY,
        ]);
        this.hemoglobine = this.formatZero(surv.hemoglobine, [...utils_1.NEGATIVE_ARRAY, ...utils_1.NEGATIVE_FLOAT_ARRAY]);
        this.hemocue = this.formatZero(surv.hemocue);
        this.shockIndex = this.formatZero(surv.shockIndex);
        this.choc_date = luxon_1.DateTime.fromISO(surv.choc_date).isValid
            ? luxon_1.DateTime.fromISO(surv.choc_date).setZone('Europe/Paris').setLocale('fr-fr').toFormat('dd/LL/yy')
            : null;
        this.choc_time = luxon_1.DateTime.fromISO(surv.choc_time).isValid
            ? luxon_1.DateTime.fromISO(surv.choc_time).setZone('Europe/Paris').setLocale('fr-fr').toFormat('HH:mm')
            : null;
        this.nb_choc_total = this.format(surv.nb_choc_total);
        this.choc_index = this.formatZero(surv.choc_index, [...utils_1.NEGATIVE_ARRAY, ...utils_1.NEGATIVE_FLOAT_ARRAY]);
        this.pupilles = this.format(surv.pupilles, [...utils_1.EMPTY_STRING_ARRAY, ...utils_1.ZERO_ARRAY]);
        this.pupilles_bilateral = this.formatZero(surv?.pupilles_bilateral);
        this.glasgow = this.format(surv.glasgow, [...utils_1.NEGATIVE_ARRAY, ...utils_1.ZERO_ARRAY]);
        this.gla_yeux = this.format(surv.gla_yeux, [...utils_1.NEGATIVE_ARRAY, ...utils_1.ZERO_ARRAY]);
        this.gla_parole = this.format(surv.gla_parole, [...utils_1.NEGATIVE_ARRAY, ...utils_1.ZERO_ARRAY]);
        this.gla_motricite = this.format(surv.gla_motricite, [...utils_1.NEGATIVE_ARRAY, ...utils_1.ZERO_ARRAY]);
        this.glycemie = this.formatZero(surv.glycemie, [...utils_1.NEGATIVE_ARRAY, ...utils_1.NEGATIVE_FLOAT_ARRAY]);
        this.glycemie_unite = [0, 1, 2].includes(surv.glycemie_unite)
            ? this.formatOnly(surv.glycemie_unite, [...utils_1.NEGATIVE_ARRAY, ...utils_1.NEGATIVE_FLOAT_ARRAY])
            : this.formatOnly(commonGlyUnit, [...utils_1.NEGATIVE_ARRAY, ...utils_1.NEGATIVE_FLOAT_ARRAY]);
        this.avpu = this.formatZero(surv.avpu, [...utils_1.NEGATIVE_ARRAY]);
        this.hbco = this.formatZero(surv.hbco, [...utils_1.NEGATIVE_ARRAY]);
        this.trc = this.formatZero(surv.trc, [...utils_1.NEGATIVE_ARRAY]);
        this.paleur = this.formatZero(surv.paleur, [...utils_1.EMPTY_STRING_ARRAY]);
        this.score_sedation = this.formatZero(surv.score_sedation, [...utils_1.NEGATIVE_ARRAY, ...utils_1.NEGATIVE_FLOAT_ARRAY]);
        this.hemoglobine_float = this.formatZero(surv.hemoglobine_float, [...utils_1.NEGATIVE_ARRAY, ...utils_1.NEGATIVE_FLOAT_ARRAY]);
        this.temperature = this.formatZero(surv.temperature, [...utils_1.NEGATIVE_ARRAY, ...utils_1.NEGATIVE_FLOAT_ARRAY]);
        this.temperature_zone = null;
        this.eva = this.formatZero(surv.eva, [...utils_1.NEGATIVE_ARRAY]);
        this.comment = surv.commentaire || null;
        this.apgar_score = this.formatZero(surv.apgar_score, [...utils_1.NEGATIVE_ARRAY, ...utils_1.NEGATIVE_FLOAT_ARRAY]);
        this.malinas = this.formatZero(surv.malinas, [...utils_1.NEGATIVE_ARRAY, ...utils_1.NEGATIVE_FLOAT_ARRAY]);
        this.nihss = this.formatZero(surv.nihss, [...utils_1.NEGATIVE_ARRAY, ...utils_1.NEGATIVE_FLOAT_ARRAY]);
        this.apgar = {
            fc: this.format(surv.apgar?.fc, [...utils_1.ZERO_ARRAY]),
            fr: this.format(surv.apgar?.fr, [...utils_1.ZERO_ARRAY]),
            tonus: this.format(surv.apgar?.tonus, [...utils_1.ZERO_ARRAY]),
            reactivite: this.format(surv.apgar?.reactivite, [...utils_1.ZERO_ARRAY]),
            coloration: this.format(surv.apgar?.coloration, [...utils_1.ZERO_ARRAY]),
        };
        this.obst = {
            malinas_parite: this.format(surv?.malinas_parite, [...utils_1.ZERO_ARRAY, ...utils_1.NEGATIVE_ARRAY]),
            malinas_travail: this.format(surv?.malinas_travail, [...utils_1.ZERO_ARRAY, ...utils_1.NEGATIVE_ARRAY]),
            malinas_duree_contraction: this.format(surv?.malinas_duree_contraction, [...utils_1.ZERO_ARRAY, ...utils_1.NEGATIVE_ARRAY]),
            malinas_intervalle_contraction: this.format(surv?.malinas_intervalle_contraction, [
                ...utils_1.ZERO_ARRAY,
                ...utils_1.NEGATIVE_ARRAY,
            ]),
            malinas_perte_eaux: this.format(surv?.malinas_perte_eaux, [...utils_1.ZERO_ARRAY, ...utils_1.NEGATIVE_ARRAY]),
            nombre_pars: this.format(surv?.nombre_pars, [...utils_1.ZERO_ARRAY, ...utils_1.NEGATIVE_ARRAY]),
            duree_contractions: this.format(surv?.duree_contractions, [...utils_1.ZERO_ARRAY, ...utils_1.NEGATIVE_ARRAY]),
            intervalle_contraction: this.format(surv?.intervalle_contraction, [
                ...utils_1.EMPTY_STRING_ARRAY,
                ...utils_1.ZERO_ARRAY,
                ...utils_1.NEGATIVE_ARRAY,
            ]),
            intervalle_contraction_sec: this.format(surv?.intervalle_contraction_sec, [...utils_1.ZERO_ARRAY, ...utils_1.NEGATIVE_ARRAY]),
            contraction: this.format(surv?.contraction, [...utils_1.ZERO_ARRAY, ...utils_1.NEGATIVE_ARRAY]),
            perte_eaux: this.format(surv?.perte_eaux, [...utils_1.ZERO_ARRAY, ...utils_1.NEGATIVE_ARRAY]),
        };
    }
    static extractHours(s) {
        return s.surv_heure
            ? luxon_1.DateTime.fromISO(s.surv_heure).setZone('Europe/Paris').setLocale('fr-fr').toFormat('HH') ?? '--'
            : s.heure.padStart(2, '0');
    }
    static extractMinutes(s) {
        return s.surv_heure
            ? luxon_1.DateTime.fromISO(s.surv_heure).setZone('Europe/Paris').setLocale('fr-fr').toFormat('mm') ?? '--'
            : s.minute.padStart(2, '0');
    }
}
exports.SurveillanceImpl = SurveillanceImpl;
